import React, {useEffect, useMemo, useState, useRef} from 'react'
import { Flex } from '../../../vanilla'
import {HitsProps, useHits, usePagination} from 'react-instantsearch-hooks-web'
import {
  useGlobalStore,
  useProductStore,
  useRapidDeliveryStore,
} from '../../../store/hooks/useStore'
import {Hit} from '../../../store/ProductStore'
import {Nullable} from '../../../types/utils'
import {SlotContentItem} from '../../../types/cms'
import {transformHit} from '../util/transformHit'
import {HITS_PER_PAGE} from '../../../constants'
import { detectScreenSize, isServer } from '../../../utils/utils'
import {useHitsProductBadges} from '../util/useHitsProductBadges'
import { buildArrayForPLP, createPLPSections, getRowSlotPosition } from '../../../pages/category/partials/display-slots'
import { DefaultContentBody } from 'dc-delivery-sdk-js'
import { calculateSlicePoints, getRowSlices, modifySlotInGridPromoCmsContent } from '../util/utils'
import { HitsOptions } from '../../../types/algolia'

export const Hits: React.FC<
  HitsProps<Hit> & {
    itemListName?: string
    itemListId?: string
    inGridPromos?: Record<string, any>
    categoryId?: Nullable<string>
    searchQuery?: Nullable<string>
    options?: HitsOptions
    onHitsUpdated?: (hits: Array<Hit>) => void
    rowSlots?: DefaultContentBody[]
    maxSponsoredProducts?: number
  }
> = ({
  inGridPromos,
  className,
  categoryId,
  searchQuery,
  itemListName,
  itemListId,
  options,
  onHitsUpdated,
  rowSlots,
  maxSponsoredProducts
}) => {
  const {hits} = useHits<Hit>()
  const {selectedStoreId, selectedPriceDayOffset, selectedRegion} = useGlobalStore()
  const {storeHitsAsProducts} = useProductStore()
  const {currentRefinement} = usePagination()
  const {isRapidPricingActive} = useRapidDeliveryStore()
  const transformedHits = hits?.map((hit) => transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion, isRapidPricingActive)) as Hit[]
  const [isLoading, setIsLoading] = useState(false)
  const prevStoreIdRef = useRef(selectedStoreId)
  const [pageStartQuery, setPageStartQuery] = useState<string | null>(null)
  const screenSize: "desktop" | "tablet" | "mobile" | undefined = isServer ? 'desktop' : detectScreenSize()

  useEffect(() => {
    if (prevStoreIdRef.current !== selectedStoreId && selectedStoreId !== '0') {
      setIsLoading(true)
      setTimeout(() => {
        setIsLoading(false)
      }, 500)
    }
    prevStoreIdRef.current = selectedStoreId
  }, [selectedStoreId])

  // Fetch product badges for the hits
  useHitsProductBadges(transformedHits)

  useEffect(() => {
    storeHitsAsProducts(transformedHits)
  }, [transformedHits])

  useEffect(() => {
    setPageStartQuery(`${HITS_PER_PAGE * currentRefinement}`)
  }, [currentRefinement])

  // Places the inGridCard in the correct place within the list of hits
  const hitsAndPromos: Array<Hit | SlotContentItem> = useMemo(() => {
    if (!inGridPromos) {
      return transformedHits
    }

    const _hits: Array<Hit | SlotContentItem> = [...transformedHits]

    // Config for the positions and visibility of inGridPromos based on screen size
    const promoInsertConfig: Record<number, Record<string, { position: number; showOnDesktop: boolean }>> = {
      0: {
        mobile: { position: 2, showOnDesktop: false },
        tablet: { position: 3, showOnDesktop: false },
        desktop: { position: 3, showOnDesktop: true },
      },
      1: {
        mobile: { position: 11, showOnDesktop: false },
        tablet: { position: 11, showOnDesktop: false },
        desktop: { position: 14, showOnDesktop: true },
      },
    }

    // Iterate over inGridPromos and use the mapping configuration
    inGridPromos?.default?.inGridPositions?.forEach((gridPromoContentData: SlotContentItem, index: number) => {
      const config = screenSize && promoInsertConfig[index]?.[screenSize]
      if (config) {
        _hits.splice(
          config.position,
          0,
          // @TODO This is ignored from TS checking here because it requires an Algolia rewrite, which is in future plans. Remove the ignore when this happens
          // @ts-ignore
          modifySlotInGridPromoCmsContent(gridPromoContentData, {
            showOnDesktop: config.showOnDesktop,
            index: gridPromoContentData.contentSpotPosition,
            slotIndex: inGridPromos?.originalSlotIndex
          }),
        )
      }
    })
    return _hits
  }, [transformedHits, inGridPromos])

  useEffect(() => {
    if (onHitsUpdated && transformedHits.length) {
      onHitsUpdated(transformedHits)
    }
  }, [transformedHits, onHitsUpdated])

  const flatListInfo = {
    hitsAndPromos,
    itemListName,
    itemListId,
    categoryId,
    searchQuery,
    pageStartQuery,
    options,
    maxSponsoredProducts: maxSponsoredProducts ?? 0
  }

// Calculate where to slice the hits based on screensize
const slicePoints = calculateSlicePoints(screenSize)
// Slice the hits 
const rowEndIndexes = slicePoints && getRowSlices(hitsAndPromos, slicePoints)
// Decide which piece of content should sit in each slot
const rowSlotPositions = getRowSlotPosition(rowSlots)
// Create the list of hits and InGridCards for the page
const flatList = buildArrayForPLP(flatListInfo)

  const createPLPSectionInfo = {
    rowEndIndexes,
    flatList,
    rowSlotPositions,
    className: className ?? '',
    screenSize
  }

  const sections = screenSize && createPLPSections(createPLPSectionInfo)

  return (
    <Flex direction="column" width="full">
      {sections}
    </Flex>
  ) 
}
